import { useBehavior, useButton } from '@libs/hooks';
import { TButtonData, TCommonOption, TWidget } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, ElementType, MouseEventHandler } from 'react';
import { TButtonRowEmphasised } from '../../types/data';
import { TRowOption } from '../../types/options';
import { getInlineStyles } from '../../utils/styles';
import { Visual } from '../Image/Visual';
import { Tag } from '../UI/Tag/Tag';
import { TextCommon } from '../UI/Text/TextCommon';
import styles from './styles/rowEmphasised.module.css';

type TButtonRowEmphasisedWidget<TD, TO> = (
    | {
          isWidget: true;
          widget: TWidget<TD, TO>;
      }
    | ({
          isWidget: false;
          handleClick: MouseEventHandler;
          title?: string;
          expired?: boolean;
          subtitle?: string;
          meta?: string;
          iconName?: string;
          metaStyle?: TRowOption['metaVariant'];
          metaHierarchy?: TRowOption['metaHierarchy'];
      } & TCommonOption)
) & {
    as?: ElementType;
};

export const RowEmphasised = (props: TButtonRowEmphasisedWidget<TButtonRowEmphasised, TRowOption>) => {
    const { isWidget, as } = props;
    const finalWidget = isWidget
        ? props.widget
        : ({
              uid: '',
              data: {
                  iconName: props.iconName,
                  expired: props.expired,
                  messages: {
                      title: props.title,
                      subtitle: props.subtitle,
                      meta: props.meta,
                  },
              },
              options: {
                  metaVariant: props.metaStyle,
                  isDisabled: props.isDisabled,
              },
          } as TWidget<TButtonRowEmphasised, TRowOption>);

    const { handleClick } = useButton(finalWidget);
    const { isDisabled, isHidden } = useBehavior(finalWidget as TWidget<TButtonData>);
    const expiredOrDisabled = finalWidget.data?.expired ?? isDisabled ?? false;
    const RowButtonComponent = as ?? 'button';

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <RowButtonComponent
                data-test={finalWidget.uid}
                aria-disabled={isDisabled}
                aria-label={finalWidget.data?.messages?.title}
                aria-hidden={isHidden}
                className={clsx(styles.rowEmphasised, isDisabled && styles.disabled)}
                style={getInlineStyles({ margin: finalWidget.options?.margin })}
                onClick={handleClick}
            >
                {(finalWidget.data?.iconName || finalWidget.data?.avatar) && (
                    <Visual
                        isWidget={false}
                        dataTest={`icon-${finalWidget.uid}`}
                        avatar={finalWidget.data?.avatar}
                        iconName={finalWidget.data?.iconName}
                        rightMargin
                        iconHierarchy={'secondary'}
                        iconVariant={'circle'}
                        size={'small'}
                        isDisabled={expiredOrDisabled}
                    />
                )}
                <div className={styles.rowContent}>
                    <div className={styles.row} data-test={`title-${finalWidget.uid}`}>
                        <TextCommon
                            isWidget={false}
                            content={finalWidget.data?.messages?.title}
                            options={{
                                variant: 'main',
                                textColor: expiredOrDisabled ? 'grey_40_lc' : 'black',
                                ellipsis: true,
                            }}
                        />
                    </div>
                    {finalWidget.data?.messages?.subtitle && (
                        <div className={styles.row} data-test={`subtitle-${finalWidget.uid}`}>
                            <TextCommon
                                isWidget={false}
                                content={finalWidget.data.messages.subtitle}
                                options={{
                                    variant: 'secondary',
                                    textColor: expiredOrDisabled ? 'grey_40_lc' : 'grey_72_lc',
                                    ellipsis: true,
                                }}
                            />
                        </div>
                    )}
                </div>
                {finalWidget.data?.messages?.meta && (
                    <div className={clsx(styles.row, styles.alignRight)} data-test={`meta-${finalWidget.uid}`}>
                        <Tag
                            metaText={finalWidget.data?.messages?.meta}
                            metaVariant={finalWidget.options?.metaVariant}
                            metaHierarchy={finalWidget.options?.metaVariant === 'tag' ? 'secondary' : undefined}
                            disabled={expiredOrDisabled}
                        />
                    </div>
                )}
            </RowButtonComponent>
        </Activity>
    );
};
